/** @typedef {'neutral' | 'brand' | 'semantic' | 'classic'} PayslipColorGroup */

/**
 * Preset text colors for payslip blocks.
 * @type {Record<string, { hex: string, group: PayslipColorGroup }>}
 */
export const PAYSLIP_TEXT_COLORS = {
  default: { hex: "#334155", group: "neutral" },
  muted: { hex: "#64748b", group: "neutral" },
  dark: { hex: "#0f172a", group: "neutral" },
  light: { hex: "#94a3b8", group: "neutral" },
  primary: { hex: "#6366f1", group: "brand" },
  indigo: { hex: "#4f46e5", group: "brand" },
  violet: { hex: "#7c3aed", group: "brand" },
  sky: { hex: "#0284c7", group: "brand" },
  teal: { hex: "#0d9488", group: "brand" },
  success: { hex: "#059669", group: "semantic" },
  danger: { hex: "#dc2626", group: "semantic" },
  warning: { hex: "#d97706", group: "semantic" },
  info: { hex: "#2563eb", group: "semantic" },
  navy: { hex: "#1e3a8a", group: "classic" },
  burgundy: { hex: "#9f1239", group: "classic" },
  forest: { hex: "#166534", group: "classic" },
  orange: { hex: "#ea580c", group: "classic" },
  rose: { hex: "#e11d48", group: "classic" },
  brown: { hex: "#78350f", group: "classic" },
};

export const PAYSLIP_TEXT_COLOR_CUSTOM = "custom";

/** @type {PayslipColorGroup[]} */
export const PAYSLIP_COLOR_GROUP_ORDER = ["neutral", "brand", "semantic", "classic"];

/** @param {unknown} raw */
export const isValidHexColor = (raw) => /^#[0-9A-Fa-f]{6}$/.test(String(raw || "").trim());

/** @param {unknown} raw @returns {string} */
export const normalizeCustomHex = (raw) => {
  const s = String(raw || "").trim();
  if (!s) return "#6366f1";
  const withHash = s.startsWith("#") ? s : `#${s}`;
  return isValidHexColor(withHash) ? withHash.toLowerCase() : "#6366f1";
};

/** @param {unknown} raw */
export const normalizeTextColorKey = (raw) => {
  const key = String(raw || "").trim();
  if (key === PAYSLIP_TEXT_COLOR_CUSTOM) return PAYSLIP_TEXT_COLOR_CUSTOM;
  return PAYSLIP_TEXT_COLORS[key] ? key : "default";
};

/**
 * @param {{ textColor?: string, customTextColor?: string }} block
 * @returns {string}
 */
export const resolveTextColorHex = (block) => {
  const key = normalizeTextColorKey(block?.textColor);
  if (key === PAYSLIP_TEXT_COLOR_CUSTOM) {
    return normalizeCustomHex(block?.customTextColor);
  }
  return PAYSLIP_TEXT_COLORS[key].hex;
};

/**
 * @param {PayslipColorGroup} groupId
 * @returns {string[]}
 */
export const getColorKeysForGroup = (groupId) =>
  Object.entries(PAYSLIP_TEXT_COLORS)
    .filter(([, meta]) => meta.group === groupId)
    .map(([id]) => id);
